<script lang="ts">
	import { Handle, Position } from '@xyflow/svelte';

	import { devices, rooms } from 'src/stores';
	import { resolveDeviceInStore } from '../helpers/resolveDeviceInStore';

	import { flowCanvasTheme } from '../../../stores/flow_editor';
	import { NODE_COLORS } from '../types/flow-types';
	import type { NodeType, NodeData } from '../types/flow-types';
	import {
		handleStyleAtPx,
		multiHandleStyle,
		type FlowBranchOutputs,
		type FlowNodePort,
	} from '../helpers/flowBranchHandleStyles';
	import {
		formatFlowCategoryTitle,
		normalizeFlowFaIcon,
		resolveFlowNodeAvatar,
		type FlowNodeAvatarConfig,
	} from '../helpers/flowNodeCardPresentation';
	import { getFlowCardVisualProfile } from '../helpers/flowCardVisualLanguage';
	import { getFlowCardDefinition } from '../core/card-engine/registry';
	import FlowNodeCardLayout from './FlowNodeCardLayout.svelte';
	import { stripXyflowShellProps } from './xyflowDomProps';

	export let id: string;
	export let type: NodeType;
	export let data: NodeData;
	export let selected: boolean = false;
	export let validated: boolean = true;
	export let errors: string[] = [];
	export let hideInputPort: boolean = false;
	export let hideOutputPort: boolean = false;
	/** When set, renders true/(false) branch sources instead of a single output port. */
	export let branchOutputs: FlowBranchOutputs = false;
	/** Explicit ports for nodes with dynamic inputs/outputs. */
	export let inputPorts: FlowNodePort[] | null = null;
	export let outputPorts: FlowNodePort[] | null = null;

	/** Primary card title — defaults to device name or `data.label`. */
	export let cardTitle: string | undefined = undefined;

	/** Muted category line — only when explicitly set (avoid duplicating body info). */
	export let cardDescription: string | undefined = undefined;

	/** Short summary when the card has no body slot. */
	export let subtitle: string | undefined = undefined;

	/** Configured, sentence-like explanation. Derived from the card definition by default. */
	export let sentence: string | undefined = undefined;
	export let suppressDerivedSentence = false;

	/** Human-readable node category shown as the card subtitle. */
	export let nodeTypeLabel: string | undefined = undefined;

	export let subtitleType:
		| 'default'
		| 'primary'
		| 'secondary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'disabled' = 'default';

	/** Optional header badge (right pill). */
	export let badge: string | undefined = undefined;

	export let avatarOverride: FlowNodeAvatarConfig | undefined = undefined;

	export let labelList: { icon?: string; value?: string; iconColor?: string }[] | undefined =
		undefined;

	let dividerPx = 44;
	let shellHeight = 80;
	let shellElement: HTMLElement | undefined;
	let alignedPortTops: Record<string, number> = {};

	$: nodeColor = NODE_COLORS[type] || '#666';
	$: hasError = !validated && errors.length > 0;
	$: isUnconfigured = !validated && errors.length === 0;
	$: domRest = stripXyflowShellProps($$restProps as Record<string, unknown>);
	$: categoryTitle = formatFlowCategoryTitle(type);
	$: deviceIdentity = resolveDeviceIdentity(data, $devices, $rooms);
	$: resolvedTitle =
		(cardTitle ?? deviceIdentity ?? String(data.label ?? categoryTitle)).trim() || categoryTitle;
	$: resolvedDescription = cardDescription;
	$: resolvedSubtitle = (subtitle ?? '').trim();
	$: definition = getFlowCardDefinition(type, String(data.paletteKey || ''));
	$: derivedSentence = (
		sentence ??
		(suppressDerivedSentence || (type === 'condition' && Boolean($$slots.default))
			? ''
			: definition?.summary(data as Record<string, any>)) ??
		''
	).trim();
	$: resolvedSentence =
		derivedSentence && derivedSentence.toLocaleLowerCase() !== resolvedTitle.toLocaleLowerCase()
			? derivedSentence
			: undefined;
	$: titleIcon = normalizeFlowFaIcon(hasError ? 'fa-solid fa-triangle-exclamation' : data.icon);
	$: semanticVariant = semanticVariantFor(type, hasError);
	$: visualProfile = getFlowCardVisualProfile(type, data);
	$: canvasTheme = $flowCanvasTheme;
	$: avatar = {
		...(avatarOverride ??
			resolveFlowNodeAvatar(data, titleIcon, semanticVariant, hasError, $devices)),
		backgroundColor: undefined,
		noBackground: true,
	};
	$: hasBodyContent = Boolean($$slots.default);
	$: resolvedInputPorts =
		inputPorts ?? (hideInputPort ? [] : [{ id: 'input' } satisfies FlowNodePort]);
	$: resolvedOutputPorts =
		outputPorts ??
		(branchOutputs === 'true-false'
			? [
					{ id: 'true', color: '#22c55e', label: 'if true' },
					{ id: 'false', color: '#ef4444', label: 'if false' },
				]
			: branchOutputs === 'true-only'
				? [{ id: 'true', color: '#22c55e', label: 'if true' }]
				: hideOutputPort
					? []
					: [{ id: 'output', label: linearOutputLabel(visualProfile.role) }]);

	function linearOutputLabel(role: string): string {
		if (role === 'delay' || role === 'control') return 'continue';
		if (role === 'action' || role === 'state') return 'then';
		return '';
	}

	function resolveDeviceIdentity(
		nodeData: NodeData,
		deviceStore: Record<string, any>,
		roomStore: Record<string, any>,
	): string | undefined {
		const raw = nodeData as any;
		const id =
			raw.device?.id ??
			raw.deviceId ??
			raw._device_condition?.device?.id ??
			raw.sceneCapability?.deviceId;
		if (!id) return undefined;
		const channel =
			Number(raw.device?.channel ?? raw.channel ?? raw._device_condition?.device?.channel ?? 0) ||
			0;
		const device = resolveDeviceInStore(deviceStore, String(id), channel) as any;
		const name = String(device?.name ?? raw.deviceName ?? raw.device_name ?? raw.device?.name ?? id);
		const room = roomStore?.[device?.room_id];
		const roomName = String(
			room?.name ?? device?.room_name ?? device?.roomName ?? raw.roomName ?? '',
		).trim();
		return roomName && roomName !== name ? `${name} · ${roomName}` : name;
	}

	function portStyle(
		side: 'left' | 'right',
		port: FlowNodePort,
		index: number,
		ports: FlowNodePort[],
		alignments: Record<string, number>,
	): string {
		if (port.centered) {
			const transform = side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)';
			const background = port.color ? ` background: ${port.color};` : '';
			return `top: 50%; transform: ${transform};${background}`;
		}
		const alignedTop = alignments[`${side}:${port.id}`];
		return alignedTop !== undefined
			? handleStyleAtPx(alignedTop, side, port.color || '')
			: multiHandleStyle(side, index, ports.length, shellHeight, port.color || '');
	}

	function measureAlignedPorts(node: HTMLElement, _portKey: string) {
		let frame = 0;

		const measure = () => {
			const next: Record<string, number> = {};
			const shellRect = node.getBoundingClientRect();
			const scaleY = node.offsetHeight > 0 ? shellRect.height / node.offsetHeight : 1;

			for (const [side, ports] of [
				['left', resolvedInputPorts],
				['right', resolvedOutputPorts],
			] as const) {
				for (const port of ports) {
					if (!port.alignSelector) continue;
					const anchor = node.querySelector(port.alignSelector) as HTMLElement | null;
					if (!anchor) continue;
					const anchorRect = anchor.getBoundingClientRect();
					const top = (anchorRect.top + anchorRect.height / 2 - shellRect.top) / (scaleY || 1);
					if (Number.isFinite(top)) next[`${side}:${port.id}`] = top;
				}
			}

			const keys = Object.keys(next);
			const changed =
				keys.length !== Object.keys(alignedPortTops).length ||
				keys.some((key) => Math.abs(next[key] - (alignedPortTops[key] ?? -1)) > 0.5);
			if (changed) alignedPortTops = next;
		};

		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(measure);
		};
		const observer = new ResizeObserver(schedule);
		observer.observe(node);
		schedule();

		return {
			update: schedule,
			destroy() {
				cancelAnimationFrame(frame);
				observer.disconnect();
			},
		};
	}

	$: alignedPortKey = [...resolvedInputPorts, ...resolvedOutputPorts]
		.map((port) => `${port.id}:${port.alignSelector || ''}`)
		.join('|');

	function semanticVariantFor(
		t: NodeType,
		err: boolean,
	): 'trigger' | 'condition' | 'action' | 'warning' {
		if (err) return 'warning';
		const s = String(t);
		if (s === 'trigger') return 'trigger';
		if (
			s === 'action' ||
			s === 'notificationAction' ||
			s === 'groupAction' ||
			s === 'sceneAction' ||
			s === 'alarmAction' ||
			s === 'scriptAction' ||
			s === 'virtualComponentAction'
		) {
			return 'action';
		}
		return 'condition';
	}

	function handleKeyboardOpen(event: KeyboardEvent) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		event.preventDefault();
		shellElement?.click();
	}
</script>

<div
	class="flow-node-shell flow-node-card-inner"
	class:selected
	class:error={hasError}
	class:unconfigured={isUnconfigured}
	style="--flow-node-accent: {nodeColor};"
	data-node-id={id}
	role="button"
	tabindex="0"
	aria-label={`${resolvedTitle}, ${nodeTypeLabel || categoryTitle}${hasError ? ', has errors' : ''}`}
	aria-pressed={selected}
	on:keydown={handleKeyboardOpen}
	bind:this={shellElement}
	bind:clientHeight={shellHeight}
	use:measureAlignedPorts={alignedPortKey}
	{...domRest}
>
	{#each resolvedInputPorts as port, index (port.id)}
		<Handle
			type="target"
			position={Position.Left}
			id={port.id}
			isConnectable={true}
			style={portStyle('left', port, index, resolvedInputPorts, alignedPortTops)}
		/>
	{/each}

	<div class="flow-node-card">
		<FlowNodeCardLayout
			bind:dividerOffsetPx={dividerPx}
			{canvasTheme}
			{semanticVariant}
			visualRole={visualProfile.role}
			roleLabel={visualProfile.roleLabel}
			roleIcon={visualProfile.roleIcon}
			isComposite={visualProfile.isComposite}
			title={resolvedTitle}
			sentence={resolvedSentence}
			description={resolvedDescription}
			subtitle={resolvedSubtitle || undefined}
			nodeType={nodeTypeLabel}
			{subtitleType}
			{avatar}
			{badge}
			{labelList}
			{selected}
			{hasBodyContent}
		>
			<slot />
		</FlowNodeCardLayout>
	</div>

	{#each resolvedOutputPorts as port, index (port.id)}
		<Handle
			type="source"
			position={Position.Right}
			id={port.id}
			isConnectable={true}
			style={portStyle('right', port, index, resolvedOutputPorts, alignedPortTops)}
		>
			{#if port.label}
				<span
					class="handle-label"
					class:handle-label-true={port.id === 'true'}
					class:handle-label-false={port.id === 'false'}
				>
					{port.label}
				</span>
			{/if}
		</Handle>
	{/each}

	{#if hasError}
		<div class="error-indicator" title={errors.join(', ')} role="status">
			<i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
			<span><strong>Needs attention</strong>{errors[0]}</span>
			<em>Open to fix</em>
		</div>
	{/if}
</div>

<style>
	.flow-node-shell {
		position: relative;
		display: inline-block;
		width: max-content;
		max-width: none;
		cursor: grab;
	}

	.flow-node-shell:active {
		cursor: grabbing;
	}

	.flow-node-shell.selected :global(.flow-node-advanced-card) {
		box-shadow: 0 0 0 2px rgba(47, 125, 246, 0.55);
	}

	.flow-node-card {
		position: relative;
		z-index: 0;
	}

	.flow-node-shell.error :global(.flow-node-advanced-card) {
		border-color: var(--color-danger, #ef4444);
		animation: pulse-error 1s ease-in-out infinite;
	}

	.flow-node-shell.unconfigured :global(.flow-node-advanced-card) {
		border-style: dashed;
		opacity: 0.92;
	}

	.flow-node-shell.unconfigured:hover :global(.flow-node-advanced-card) {
		opacity: 1;
	}

	.flow-node-shell :global(.svelte-flow__handle) {
		width: 13px;
		height: 13px;
		border-width: 2px;
		transition:
			width 120ms ease,
			height 120ms ease,
			box-shadow 120ms ease;
	}

	.flow-node-shell :global(.svelte-flow__handle:hover),
	.flow-node-shell :global(.svelte-flow__handle:focus-visible) {
		width: 22px;
		height: 22px;
		box-shadow: 0 0 0 5px color-mix(in srgb, var(--flow-node-accent) 22%, transparent);
		z-index: 12;
	}

	.error-indicator {
		position: absolute;
		left: 0.65rem;
		right: 0.65rem;
		bottom: -2.15rem;
		z-index: 5;
		display: flex;
		align-items: center;
		gap: 0.4rem;
		min-height: 1.75rem;
		padding: 0.25rem 0.45rem;
		border: 1px solid color-mix(in srgb, var(--color-danger, #ef4444) 60%, transparent);
		border-radius: 0.45rem;
		background: color-mix(in srgb, var(--color-danger, #ef4444) 13%, #1f2022 87%);
		color: #fecaca;
		font-size: 0.6rem;
		line-height: 1.2;
		cursor: pointer;
	}

	.error-indicator span {
		display: flex;
		min-width: 0;
		flex: 1;
		flex-direction: column;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.error-indicator em {
		color: #fff;
		font-style: normal;
		font-weight: 700;
		white-space: nowrap;
	}

	@keyframes pulse-error {
		0%,
		100% {
			border-color: var(--color-danger, #ef4444);
		}
		50% {
			border-color: #dc2626;
		}
	}

	.handle-label {
		position: absolute;
		left: 100%;
		margin-left: 0.5rem;
		font-size: 0.75rem;
		font-weight: 700;
		white-space: nowrap;
		pointer-events: none;
		opacity: 0.72;
		transition: opacity 0.2s;
		line-height: 1.2;
	}

	.handle-label-true {
		top: auto;
		bottom: calc(100% + 6px);
		transform: none;
		color: #22c55e;
	}

	.handle-label-false {
		top: calc(100% + 6px);
		bottom: auto;
		transform: none;
		color: #ef4444;
	}

	.flow-node-shell:hover .handle-label {
		opacity: 1;
	}

	@media (max-width: 640px) {
		.handle-label {
			opacity: 0.88;
			font-size: 0.68rem;
		}
	}
</style>
